import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import URLS from 'constants/urls';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import Segment from 'helpers/segment';

import messages from '../Notifications.messages';

const MARKETING_STATUS = {
  SUBSCRIBED: 'subscribed',
  UNSUBSCRIBED: 'unsubscribed',
};

export const marketingSubscriptionKeys = {
  all: ['marketingSubscription'],
};

// ignoreInternalError keeps failures local to this section (safe-default OFF on read, revert +
// snackbar on write) instead of tripping the global network snackbar / internal-error overlay.
const REQUEST_CONFIG = { ignoreInternalError: true };

async function fetchMarketingSubscription() {
  const response = await httpClient.get(URLS.user.marketingSubscription, REQUEST_CONFIG);
  return response.data?.status === MARKETING_STATUS.SUBSCRIBED;
}

async function putMarketingSubscription(optIn) {
  const status = optIn ? MARKETING_STATUS.SUBSCRIBED : MARKETING_STATUS.UNSUBSCRIBED;
  await httpClient.put(URLS.user.marketingSubscription, { status }, REQUEST_CONFIG);
  return optIn;
}

export function useMarketingSubscriptionQuery(options = {}) {
  return useQuery({
    queryKey: marketingSubscriptionKeys.all,
    queryFn: fetchMarketingSubscription,
    // Braze reads lag writes, so an auto-refetch (tab focus / reconnect) can return the stale
    // pre-update state and revert the toggle. Fetch once per page load, per the TDD.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
}

export function useUpdateMarketingSubscriptionMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: putMarketingSubscription,
    onMutate: async optIn => {
      await queryClient.cancelQueries({ queryKey: marketingSubscriptionKeys.all });
      const previous = queryClient.getQueryData(marketingSubscriptionKeys.all);
      queryClient.setQueryData(marketingSubscriptionKeys.all, optIn);
      return { previous };
    },
    onError: (error, optIn, context) => {
      queryClient.setQueryData(marketingSubscriptionKeys.all, context?.previous);
      dispatch(createErrorSnackbar(messages.marketingUpdateError));
      reportError(error);
    },
    onSuccess: optIn => {
      // A resolved PUT means the requested state was applied — trust `optIn` rather than
      // re-parsing the response body (a missing/renamed field would wrongly flip the toggle OFF)
      // or refetching (Braze reads are eventually consistent and can return stale state).
      queryClient.setQueryData(marketingSubscriptionKeys.all, optIn);
      dispatch(
        createSuccessSnackbar(optIn ? messages.marketingSubscribeSuccess : messages.marketingUnsubscribeSuccess)
      );
      Segment.event('Marketing notification update', { Type: optIn ? 'Opt in' : 'Opt Out' });
    },
  });
}
